import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  isDevMode,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { isArray, isSet, isString } from '@ngx-simple/core/typeof';
import { NgClassInterface } from '@ngx-simple/core/types';
import { CalendarCell, CalendarUserEvent, SimCalendarBodyComponent, SimCalendarCellClassFunction } from './calendar-body.component';
import { SimDateRangeSelectionStrategy, SIM_DATE_RANGE_SELECTION_STRATEGY } from './date-range-selection-strategy';
import { DateRange } from './date-selection-model';
import { createMissingDateImplError } from './datepicker-errors';
import { DateFilterFn } from './datepicker-input-base';

const DAYS_PER_WEEK = 8;

/** 一个内部组件，用于在日期选择器中显示一个月。 */
@Component({
  selector: 'sim-week-view',
  templateUrl: './week-view.component.html',
  styleUrls: ['./datepicker.component.scss'],
  exportAs: 'simWeekView',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimWeekViewComponent<D> implements AfterContentInit {
  /**
   * 在本月视图中显示的日期（忽略月份和年份以外的所有内容）.
   */
  @Input()
  get activeDate(): D {
    return this._activeDate;
  }
  set activeDate(value: D) {
    const oldActiveDate = this._activeDate;
    const validDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value)) || this._dateAdapter.today();
    this._activeDate = this._dateAdapter.clampDate(validDate, this.minDate, this.maxDate);
    if (!this._hasSameMonthAndYear(oldActiveDate, this._activeDate)) {
      this._init();
    }
  }

  /** 当前选择的日期. */
  @Input()
  get selected(): DateRange<D> | D | null {
    return this._selected;
  }
  set selected(value: DateRange<D> | D | null) {
    if (value instanceof DateRange) {
      this._selected = value;
    } else {
      this._selected = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
    }

    this._setRanges(this._selected);
  }

  /** 最小选择日期 */
  @Input()
  get minDate(): D | null {
    return this._minDate;
  }
  set minDate(value: D | null) {
    this._minDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 最大选择日期 */
  @Input()
  get maxDate(): D | null {
    return this._maxDate;
  }
  set maxDate(value: D | null) {
    this._maxDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 用于过滤哪些日期可选的函数 */
  @Input() dateFilter: DateFilterFn<D | null>;

  @Input() dateClass: SimCalendarCellClassFunction<D>;

  /** 比较范围的开始 */
  @Input() comparisonStart: D | null;

  /** 比较范围的结束 */
  @Input() comparisonEnd: D | null;

  @Input() rangeMode: boolean;

  @Input() format: string;

  /** 选择新月份时发出 */
  @Output() readonly selectedChange: EventEmitter<D> = new EventEmitter();

  /** 选择任何日期时发出 */
  @Output() readonly userSelection: EventEmitter<CalendarUserEvent<D | null>> = new EventEmitter<CalendarUserEvent<D | null>>();

  /** 在激活任何日期时发出 */
  @Output() readonly activeDateChange: EventEmitter<D> = new EventEmitter<D>();

  @ViewChild(SimCalendarBodyComponent) _calendarBody: SimCalendarBodyComponent<D>;

  /**
   * 当前选择的日期所对应的月份的日期。如果当前选择的日期在另一个月，则为空。
   */
  _selectedDate: number | null;

  /** 日期数据 */
  _weeks: CalendarCell[][] = [];

  /** 工作日的名称 */
  _weekdays: Array<{ long: string; narrow: string }>;

  /** 当前显示的日期范围的开始值 */
  _rangeStart: number | null;

  /** 当前显示的日期范围的结束值 */
  _rangeEnd: number | null;

  /** 当前显示的比较日期范围的开始值 */
  _comparisonRangeStart: number | null;

  /** 当前显示的比较日期范围的结束值 */
  _comparisonRangeEnd: number | null;

  /** 预览范围的开 */
  _previewStart: number | null;

  /** 预览范围结束 */
  _previewEnd: number | null;

  /** 用户当前是否选择日期范围 */
  _isRange: boolean;

  /** 今天是某月某日。如果今天是另一个月，则为Null */
  _todayDate: number | null;

  private _selected: DateRange<D> | D | null;
  private _activeDate: D;
  private _minDate: D | null;
  private _maxDate: D | null;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    public _dateAdapter: SimDateAdapter<D>,
    @Inject(SIM_DATE_RANGE_SELECTION_STRATEGY)
    @Optional()
    private _rangeStrategy?: SimDateRangeSelectionStrategy<D>
  ) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('SimDateAdapter');
    }
    this._activeDate = this._dateAdapter.today();
  }

  ngAfterContentInit() {
    this._init();
  }

  _init() {
    this._setRanges(this.selected);
    this._todayDate = this._getCellCompareValue(this._dateAdapter.today());
    this._initWeekdays();
    this._createWeekCells();
    this._changeDetectorRef.markForCheck();
  }

  /** 当用户激活新单元格并且需要更新预览时调用 */
  _previewChanged({ event, value: cell }: CalendarUserEvent<CalendarCell<D> | null>) {
    if (this._rangeStrategy) {
      const value = cell ? cell.rawValue : null;
      const previewRange = this._rangeStrategy.createPreview(value, this.selected as DateRange<D>, event);
      this._previewStart = this._getCellCompareValue(previewRange.start);
      this._previewEnd = this._getCellCompareValue(previewRange.end);
      this._changeDetectorRef.detectChanges();
    }
  }

  /** 选择新周时处理 */
  _dateSelected(event: CalendarUserEvent<number>) {
    console.log('_dateSelected', event.cell);
    const date = event.value;
    const selectedYear = this._dateAdapter.getYear(this.activeDate);
    const selectedMonth = this._dateAdapter.getMonth(event.cell.rawValue);

    const selectedDate = this._dateAdapter.createDate(
      selectedYear,
      selectedMonth,
      date,
      this._dateAdapter.getHours(this.activeDate),
      this._dateAdapter.getMinutes(this.activeDate),
      this._dateAdapter.getSeconds(this.activeDate)
    );

    let rangeStartDate: number | null;
    let rangeEndDate: number | null;

    if (this._selected instanceof DateRange) {
      rangeStartDate = this._getDateInCurrentMonth(this._selected.start);
      rangeEndDate = this._getDateInCurrentMonth(this._selected.end);
    } else {
      rangeStartDate = rangeEndDate = this._getDateInCurrentMonth(this._selected);
    }

    if (rangeStartDate !== date || rangeEndDate !== date) {
      this.selectedChange.emit(selectedDate);
    }

    this.userSelection.emit({ value: selectedDate, event: event.event });
  }

  _focusActiveCell() {
    this._calendarBody._focusActiveCell();
  }

  /** 当日历处于多年视图中时，处理日历主体上的按键事件 */
  _handleCalendarBodyKeydown(event: KeyboardEvent) {}

  private _createWeekCells() {
    this._weeks = [[]];
    const firstOfMonth = this._dateAdapter.createDate(
      this._dateAdapter.getYear(this.activeDate),
      this._dateAdapter.getMonth(this.activeDate),
      1
    );
    const _firstWeekOffset = (DAYS_PER_WEEK + this._dateAdapter.getDayOfWeek(firstOfMonth)) % DAYS_PER_WEEK;
    const daysInMonth = this._dateAdapter.getNumDaysInMonth(this.activeDate);
    const _lastWeekOffset = daysInMonth + _firstWeekOffset - 1;
    // 自增日期  0 => 6*8
    for (let i = 0, cell = 0; i < 48; i++, cell++) {
      if (cell === DAYS_PER_WEEK) {
        this._weeks.push([]);
        cell = 0;
      }
      const cellClass: {
        [className: string]: boolean;
      } = {};
      let days: number = i - _firstWeekOffset;
      if (_firstWeekOffset > i) {
        cellClass['sim-calendar-previous-month-cell'] = true;
        days = -(_firstWeekOffset - i);
      } else if (i > _lastWeekOffset) {
        cellClass['sim-calendar-next-month-cell'] = true;
      } else {
        cellClass['sim-calendar-cell-in-view'] = true;
      }
      const date = this._dateAdapter.addDays(firstOfMonth, days);
      const cellClasses = this.mergeDateClass(this.dateClass ? this.dateClass(date, 'month') : undefined, cellClass);
      const enabled = this._shouldEnableDate(date);
      const ariaLabel = this._dateAdapter.format(date, 'dd');
      const day = this._dateAdapter.getDate(date);
      this._weeks[this._weeks.length - 1].push(
        new CalendarCell<D>(day, day + '', ariaLabel, enabled, cellClasses, this._getCellCompareValue(date), date)
      );
    }
  }

  private mergeDateClass(
    dateClass: NgClassInterface,
    cellClass: {
      [className: string]: boolean;
    }
  ): NgClassInterface {
    // 如果没有值直接返回
    if (dateClass == null) {
      return cellClass;
    }
    // 处理字符串
    if (isString(dateClass)) {
      // 如果可以切分成数组给数组去处理
      if (dateClass.split(' ').length) {
        return this.mergeDateClass(dateClass.split(' '), cellClass);
      }
      cellClass[dateClass] = true;
      return cellClass;
    }
    // 处理字符串数组
    if (isArray(dateClass)) {
      return dateClass.reduce((classes, key) => ((classes[key] = true), classes), cellClass);
    }
    // 处理set
    if (isSet(dateClass)) {
      dateClass.forEach(key => {
        cellClass[key] = true;
      });
      return cellClass;
    }
    // 处理 {}
    return { ...cellClass, ...dateClass };
  }

  /** 为本月的日期创建日历单元格 */
  // private _createWeekCells(): void {
  //   const weekRows: WeekRow[] = [];
  //   /** 获取当前活跃时间月份第一天 */
  //   const _firstDateOfMonth = firstDayOfMonth(this.activeDate);
  //   // 获取日期偏移
  //   const firstDateOffset = (_firstDateOfMonth.getDay() + 7 - this.firstDayOfWeek) % 7;
  //   // 获取日历第一天
  //   const firstDateToShow = addDays(_firstDateOfMonth, 0 - firstDateOffset);
  //   const start = this.startDate;
  //   const end = this.endDate;
  //   const rangeMode = this.rangeMode;
  //   // 自增日期  0 => 6*7
  //   let increased = 0;
  //   // 生成周数据
  //   for (let rowIndex = 0; rowIndex < 6; rowIndex++) {
  //     const week: WeekRow = (weekRows[rowIndex] = {
  //       isActive: false,
  //       isCurrent: false,
  //       dateCells: [],
  //     });
  //     // 生成日期数据
  //     for (let colIndex = 0; colIndex < 7; colIndex++) {
  //       // 获取当天日期
  //       const current = addDays(firstDateToShow, increased++);
  //       // 判断是否是上月结束天数
  //       const isBeforeMonthYear = this.isBeforeMonthYear(current, this.activeDate);
  //       // 判断是否是下个月起始天数
  //       const isAfterMonthYear = this.isAfterMonthYear(current, this.activeDate);
  //       // 创建列
  //       const cell: DateCell = {
  //         // 当前日期
  //         value: current,
  //         // 是否选择的日期
  //         isSelected: false,
  //         // 是否禁用日期
  //         isDisabled: false,
  //         // 是否今天
  //         isToday: false,
  //         // 输入移入title提示当前日期
  //         title: format(current, this.format),
  //         // 当前日期
  //         content: `${current.getDate()}`,
  //         // 点击日期事件
  //         onClick: () => this.changeValueFromInside(current),
  //       };
  //       // 判断当前时间是不是今天
  //       if (isToday(current)) {
  //         cell.isToday = true;
  //         week.isCurrent = true;
  //       }
  //       if (rangeMode) {
  //         // 处理范围
  //         if (start) {
  //           if (sameDate(current, start, 'day')) {
  //             cell.isSelectedStartDate = true;
  //             cell.isSelected = true;
  //             week.isActive = true;
  //           }
  //           if (end) {
  //             if (sameDate(current, end, 'day')) {
  //               cell.isSelectedEndDate = true;
  //               cell.isSelected = true;
  //               week.isActive = true;
  //             } else if (afterDate(current, start, 'day') && beforeDate(current, end, 'day')) {
  //               cell.isInRange = true;
  //               week.isActive = true;
  //             }
  //           }
  //         }
  //       } else {
  //         // 处理单选
  //         if (sameDate(current, this.activeDate, 'day')) {
  //           cell.isSelected = true;
  //           week.isActive = true;
  //         }
  //       }

  //       // 设置禁用
  //       cell.isDisabled = !this._shouldEnableDate(current);
  //       // 设置日期样式
  //       cell.classMap = {
  //         [`sim-calendar-cell`]: true,
  //         [`sim-calendar-today`]: cell.isToday,
  //         [`sim-calendar-last-month-cell`]: isBeforeMonthYear,
  //         [`sim-calendar-next-month-btn-day`]: isAfterMonthYear,
  //         [`sim-calendar-selected-day`]: cell.isSelected,
  //         [`sim-calendar-disabled-cell`]: cell.isDisabled,
  //         [`sim-calendar-selected-start-date`]: !!cell.isSelectedStartDate,
  //         [`sim-calendar-selected-end-date`]: !!cell.isSelectedEndDate,
  //         [`sim-calendar-in-range-cell`]: !!cell.isInRange,
  //       };

  //       week.dateCells.push(cell);
  //     }

  //     // 设置周样式
  //     week.classMap = {
  //       [`sim-calendar-current-week`]: week.isCurrent,
  //       [`sim-calendar-active-week`]: week.isActive,
  //     };
  //   }
  //   // 生成数据
  //   this.weekRows = weekRows;
  // }

  private changeValueFromInside(date: D): void {
    if (this.rangeMode) {
      this.selectedChange.next(date);
    } else {
      if (this.activeDate !== date) {
        this.selectedChange.next(date);
      }
    }
  }

  /** 月份的日期过滤器 */
  private _shouldEnableDate(date: D): boolean {
    return (
      !!date &&
      (!this.dateFilter || this.dateFilter(date)) &&
      (!this.minDate || this._dateAdapter.compareDate(date, this.minDate) >= 0) &&
      (!this.maxDate || this._dateAdapter.compareDate(date, this.maxDate) <= 0)
    );
  }

  /**
   * Gets the date in this month that the given Date falls on.
   * Returns null if the given Date is in another month.
   */
  private _getDateInCurrentMonth(date: D | null): number | null {
    return date && this._hasSameMonthAndYear(date, this.activeDate) ? this._dateAdapter.getDate(date) : null;
  }

  /** Checks whether the 2 dates are non-null and fall within the same month of the same year. */
  private _hasSameMonthAndYear(d1: D | null, d2: D | null): boolean {
    return !!(
      d1 &&
      d2 &&
      this._dateAdapter.getMonth(d1) === this._dateAdapter.getMonth(d2) &&
      this._dateAdapter.getYear(d1) === this._dateAdapter.getYear(d2)
    );
  }

  private isBeforeMonthYear(current: D, target: D): boolean {
    if (this._dateAdapter.getYear(current) < this._dateAdapter.getYear(target)) {
      return true;
    }
    return (
      this._dateAdapter.getYear(current) === this._dateAdapter.getYear(target) &&
      this._dateAdapter.getMonth(current) < this._dateAdapter.getMonth(target)
    );
  }

  private isAfterMonthYear(current: D, target: D): boolean {
    if (this._dateAdapter.getYear(current) > this._dateAdapter.getYear(target)) {
      return true;
    }
    return (
      this._dateAdapter.getYear(current) === this._dateAdapter.getYear(target) &&
      this._dateAdapter.getMonth(current) > this._dateAdapter.getMonth(target)
    );
  }

  /** 初始化工作日 */
  private _initWeekdays() {
    const narrowWeekdays = this._dateAdapter.getDayOfWeekNames('narrow');
    const longWeekdays = this._dateAdapter.getDayOfWeekNames('long');

    this._weekdays = longWeekdays.map((long, i) => {
      return { long, narrow: narrowWeekdays[i] };
    });
  }

  /** 获取将用于一个单元格到另一个单元格的值 */
  private _getCellCompareValue(date: D | null): number | null {
    if (date) {
      const year = this._dateAdapter.getYear(date);
      const month = this._dateAdapter.getMonth(date);
      const day = this._dateAdapter.getDate(date);
      return new Date(year, month, day).getTime();
    }

    return null;
  }

  /** 根据模型值设置当前范围 */
  private _setRanges(selectedValue: DateRange<D> | D | null) {
    if (selectedValue instanceof DateRange) {
      this._rangeStart = this._getCellCompareValue(selectedValue.start);
      this._rangeEnd = this._getCellCompareValue(selectedValue.end);
      this._isRange = true;
    } else {
      this._rangeStart = this._rangeEnd = this._getCellCompareValue(selectedValue);
      this._isRange = false;
    }

    this._comparisonRangeStart = this._getCellCompareValue(this.comparisonStart);
    this._comparisonRangeEnd = this._getCellCompareValue(this.comparisonEnd);
  }
}
